import { useRef } from 'react';
import { categories } from '@/mocks/categories';

export default function CategoryShowcase() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="w-full py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="lg:w-[25%] flex-shrink-0 flex flex-col justify-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              Browse{' '}
              <span className="text-primary-500">14 Categories</span>
              {' '}of Curated Finds
            </h2>
            <p className="text-foreground-600 text-base leading-relaxed">
              From sneakers to accessories, every category is organized and updated 
              regularly so you never miss a trending find.
            </p>
            <div className="hidden lg:flex items-center gap-3 mt-6">
              <button
                onClick={() => scroll('left')}
                className="w-10 h-10 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-foreground-700"></i>
              </button>
              <button
                onClick={() => scroll('right')}
                className="w-10 h-10 rounded-full bg-primary-500 hover:bg-primary-600 flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-line text-background-50 dark:text-foreground-950"></i>
              </button>
            </div>
          </div>

          <div className="lg:w-[75%] relative">
            <div
              ref={scrollRef}
              className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {categories.map((cat, idx) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex-shrink-0 w-[220px] md:w-[240px] snap-start group cursor-pointer"
                >
                  <div className="rounded-2xl bg-background-50 border border-background-200/60 overflow-hidden transition-all duration-300 hover:-translate-y-2">
                    <div className="w-full h-[220px] md:h-[240px] overflow-hidden bg-background-100">
                      <img
                        src={cat.imageUrl}
                        alt={cat.name}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-foreground-900 text-base mb-1">{cat.name}</h3>
                      <p className="text-foreground-500 text-xs mb-3">{cat.description}</p>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                        {cat.itemCount} finds
                        <i className="ri-arrow-right-up-line text-xs w-3 h-3 flex items-center justify-center"></i>
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}